'use client'

import { useMemo, useRef, useState } from 'react'
import { calcularCustoFuncionario, CPP_MEI, FGTS } from '@/lib/calculos/custo-funcionario'
import { DAS_CONFIG } from '@/lib/config/das'
import {
  AvisoBox,
  brl,
  DetailRow,
  LeadCapture,
  Eyebrow,
  MobileResultBar,
  MoneyField,
  parseValor,
  ResultCard,
  RowStrong,
  SoftExportBar,
  Stepper,
  useAnimatedNumber,
  useCalcTelemetry,
} from '@/components/calc'
import type { CartaoPayload } from '@/lib/export/types'

const SALARIO_MINIMO = DAS_CONFIG.salarioMinimo
const pct = (n: number) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`

export function CustoFuncionarioClient() {
  const [funcionarios, setFuncionarios] = useState(1)
  const [salario, setSalario] = useState(String(SALARIO_MINIMO).replace('.', ','))
  const [valeTransporte, setValeTransporte] = useState('')
  const [faturamentoMes, setFaturamentoMes] = useState('')

  const r = useMemo(
    () =>
      calcularCustoFuncionario({
        funcionarios,
        salario: parseValor(salario),
        valeTransporte: parseValor(valeTransporte),
        faturamentoMes: parseValor(faturamentoMes),
        ano: 2027,
      }),
    [funcionarios, salario, valeTransporte, faturamentoMes],
  )

  const temResultado = parseValor(salario) > 0
  const abaixoDoMinimo = temResultado && parseValor(salario) < SALARIO_MINIMO
  const totalAnim = useAnimatedNumber(temResultado ? r.totalMes : 0)

  useCalcTelemetry(
    'custo_funcionario',
    temResultado,
    `${funcionarios} ${funcionarios === 1 ? 'funcionário' : 'funcionários'}: ${brl(r.totalMes)}/mês`,
  )

  const buildCartao = (): CartaoPayload => ({
    nome: 'custo-funcionario-mei',
    heroLabel: funcionarios === 1 ? 'Seu funcionário custa' : 'Seus 2 funcionários custam',
    heroValue: brl(r.totalMes),
    heroSuffix: '/mês',
    tone: 'brand',
    linhas: [
      { label: 'Salário de cada um', value: brl(parseValor(salario)) },
      { label: 'Encargos (3% INSS + 8% FGTS)', value: brl(r.encargosMes * funcionarios) },
      { label: '13º e 1/3 de férias (reserva do mês)', value: brl(r.provisaoMes * funcionarios) },
      { label: 'Custo no ano', value: brl(r.totalAno), strong: true, tone: 'brand' },
    ],
  })

  const cardRef = useRef<HTMLElement>(null)

  return (
    <div className="grid grid-cols-1 gap-[18px] pb-24 lg:grid-cols-2 lg:items-start lg:pb-0">
      {/* ── INPUTS ── */}
      <section className="rounded-2xl border border-line bg-surface p-6 shadow-soft">
        <Stepper
          label="Quantos funcionários?"
          value={funcionarios}
          onChange={setFuncionarios}
          min={1}
          max={2}
        />
        {funcionarios === 2 && (
          <p className="-mt-2 mb-4 text-[12px] leading-snug text-ink-mute">
            O segundo funcionário só é permitido a partir de 1º de janeiro de 2027 (Lei Complementar
            238/2026). Em 2026 o limite ainda é 1.
          </p>
        )}
        <MoneyField
          label="Salário de cada um"
          value={salario}
          onChange={setSalario}
          hint={`O MEI só pode pagar o salário mínimo (${brl(SALARIO_MINIMO)} em 2026) ou o piso da categoria.`}
        />
        <MoneyField
          label="Vale-transporte por mês (opcional)"
          value={valeTransporte}
          onChange={setValeTransporte}
          hint="Quanto você gasta comprando o VT de cada um. Até 6% do salário é descontado do funcionário."
        />
        <MoneyField
          label="Seu faturamento médio por mês (opcional)"
          value={faturamentoMes}
          onChange={setFaturamentoMes}
          hint="Pra ver quanto da sua receita a folha vai comer."
        />
      </section>

      {/* ── RESULTADO ── */}
      <section ref={cardRef} className="flex flex-col rounded-2xl border border-line bg-bg-alt p-[22px] shadow-soft">
        <Eyebrow icon="user" tone="muted" className="mb-3">
          Custo real da folha
        </Eyebrow>

        <ResultCard
          tone="brand"
          heroLabel={funcionarios === 1 ? 'Seu funcionário custa' : 'Seus 2 funcionários custam'}
          heroValue={brl(totalAnim)}
          heroSuffix="/mês"
        >
          <DetailRow label={`Salário${funcionarios > 1 ? ' (cada)' : ''}`} value={brl(parseValor(salario))} />
          <DetailRow
            label={`INSS patronal (${pct(CPP_MEI * 100)}) + FGTS (${pct(FGTS * 100)})`}
            value={brl(r.encargosMes)}
          />
          {r.valeTransporteEmpresa > 0 && (
            <DetailRow label="Vale-transporte (parte da empresa)" value={brl(r.valeTransporteEmpresa)} />
          )}
          <DetailRow label="Reserva pra 13º e 1/3 de férias" value={brl(r.provisaoMes)} />
          <DetailRow label="Sai do caixa no mês (por pessoa)" value={brl(r.caixaMes)} />
          <RowStrong label="Custo no ano (todos)" value={brl(r.totalAno)} tone="brand" />
        </ResultCard>

        {abaixoDoMinimo && (
          <AvisoBox tone="neg" icon="alert" className="mt-4">
            O MEI não pode pagar menos que o salário mínimo ({brl(SALARIO_MINIMO)}). Use o mínimo ou o
            piso da categoria.
          </AvisoBox>
        )}

        {temResultado && (
          <AvisoBox tone="brand" icon="bulb" className="mt-4">
            {r.percentFaturamento !== null ? (
              <>
                A folha leva {pct(r.percentFaturamento)} do seu faturamento.{' '}
              </>
            ) : null}
            No ano, ela consome {pct(r.percentTeto)} do teto de {brl(r.limiteAno)} de 2027 — lembre
            que o teto é sobre o que entra, então contratar só faz sentido se a pessoa ajudar a
            faturar mais do que custa.
          </AvisoBox>
        )}

        <SoftExportBar buildCartao={buildCartao} />
        <LeadCapture show={temResultado} source="custo-funcionario-mei" alertType="das" />
      </section>

      {temResultado && <MobileResultBar payload={buildCartao()} cardRef={cardRef} />}
    </div>
  )
}
